import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  translate: (key: string) => string;
  isRTL: boolean;
}

const defaultLanguage: Language = {
  code: 'en',
  name: 'English',
  nativeName: 'English',
  flag: '🇺🇸'
};

// Translation dictionaries for each language
const translations: { [key: string]: { [key: string]: string } } = {
  en: {
    'app.name': 'πPi Dating',
    'nav.home': 'Home',
    'nav.matches': 'Matches',
    'nav.chat': 'Chat',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',
    'home.title': 'Find your perfect match',
    'matches.title': 'Your Matches',
    'chat.title': 'Messages',
    'profile.title': 'Profile',
    'settings.title': 'Settings'
  },
  ta: {
    'app.name': 'πPi டேட்டிங்',
    'nav.home': 'முகப்பு',
    'nav.matches': 'பொருத்தங்கள்',
    'nav.chat': 'அரட்டை',
    'nav.profile': 'சுயவிவரம்',
    'nav.settings': 'அமைப்புகள்',
    'home.title': 'உங்கள் சரியான பொருத்தத்தைக் கண்டறியுங்கள்',
    'matches.title': 'உங்கள் பொருத்தங்கள்',
    'chat.title': 'செய்திகள்',
    'profile.title': 'சுயவிவரம்',
    'settings.title': 'அமைப்புகள்'
  },
  de: {
    'app.name': 'πPi Dating',
    'nav.home': 'Startseite',
    'nav.matches': 'Matches',
    'nav.chat': 'Chat',
    'nav.profile': 'Profil',
    'nav.settings': 'Einstellungen',
    'home.title': 'Finde dein perfektes Match',
    'matches.title': 'Deine Matches',
    'chat.title': 'Nachrichten',
    'profile.title': 'Profil',
    'settings.title': 'Einstellungen'
  },
  it: {
    'app.name': 'πPi Dating',
    'nav.home': 'Home',
    'nav.matches': 'Match',
    'nav.chat': 'Chat',
    'nav.profile': 'Profilo',
    'nav.settings': 'Impostazioni',
    'home.title': 'Trova la tua anima gemella',
    'matches.title': 'I tuoi Match',
    'chat.title': 'Messaggi',
    'profile.title': 'Profilo',
    'settings.title': 'Impostazioni'
  },
  fr: {
    'app.name': 'πPi Dating',
    'nav.home': 'Accueil',
    'nav.matches': 'Matchs',
    'nav.chat': 'Chat',
    'nav.profile': 'Profil',
    'nav.settings': 'Paramètres',
    'home.title': 'Trouvez votre âme sœur',
    'matches.title': 'Vos Matchs',
    'chat.title': 'Messages',
    'profile.title': 'Profil',
    'settings.title': 'Paramètres'
  },
  es: {
    'app.name': 'πPi Dating',
    'nav.home': 'Inicio',
    'nav.matches': 'Matches',
    'nav.chat': 'Chat',
    'nav.profile': 'Perfil',
    'nav.settings': 'Configuración',
    'home.title': 'Encuentra tu pareja perfecta',
    'matches.title': 'Tus Matches',
    'chat.title': 'Mensajes',
    'profile.title': 'Perfil',
    'settings.title': 'Configuración'
  },
  vi: {
    'app.name': 'πPi Dating',
    'nav.home': 'Trang chủ',
    'nav.matches': 'Kết đôi',
    'nav.chat': 'Trò chuyện',
    'nav.profile': 'Hồ sơ',
    'nav.settings': 'Cài đặt',
    'home.title': 'Tìm người bạn đời hoàn hảo',
    'matches.title': 'Kết đôi của bạn',
    'chat.title': 'Tin nhắn',
    'profile.title': 'Hồ sơ',
    'settings.title': 'Cài đặt'
  },
  id: {
    'app.name': 'πPi Dating',
    'nav.home': 'Beranda',
    'nav.matches': 'Kecocokan',
    'nav.chat': 'Obrolan',
    'nav.profile': 'Profil',
    'nav.settings': 'Pengaturan',
    'home.title': 'Temukan pasangan sempurna Anda',
    'matches.title': 'Kecocokan Anda',
    'chat.title': 'Pesan',
    'profile.title': 'Profil',
    'settings.title': 'Pengaturan'
  },
  ms: {
    'app.name': 'πPi Dating',
    'nav.home': 'Laman Utama',
    'nav.matches': 'Padanan',
    'nav.chat': 'Sembang',
    'nav.profile': 'Profil',
    'nav.settings': 'Tetapan',
    'home.title': 'Cari pasangan sempurna anda',
    'matches.title': 'Padanan Anda',
    'chat.title': 'Mesej',
    'profile.title': 'Profil',
    'settings.title': 'Tetapan'
  },
  zh: {
    'app.name': 'πPi 约会',
    'nav.home': '首页',
    'nav.matches': '匹配',
    'nav.chat': '聊天',
    'nav.profile': '个人资料',
    'nav.settings': '设置',
    'home.title': '找到你的完美伴侣',
    'matches.title': '你的匹配',
    'chat.title': '消息',
    'profile.title': '个人资料',
    'settings.title': '设置'
  },
  pa: {
    'app.name': 'πPi ਡੇਟਿੰਗ',
    'nav.home': 'ਘਰ',
    'nav.matches': 'ਮੈਚ',
    'nav.chat': 'ਚੈਟ',
    'nav.profile': 'ਪ੍ਰੋਫਾਈਲ',
    'nav.settings': 'ਸੈਟਿੰਗਾਂ',
    'home.title': 'ਆਪਣਾ ਸੰਪੂਰਨ ਮੈਚ ਲੱਭੋ',
    'matches.title': 'ਤੁਹਾਡੇ ਮੈਚ',
    'chat.title': 'ਸੰਦੇਸ਼',
    'profile.title': 'ਪ੍ਰੋਫਾਈਲ',
    'settings.title': 'ਸੈਟਿੰਗਾਂ'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(defaultLanguage);

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('piDatingLanguage');
    if (savedLanguage) {
      try {
        const parsed = JSON.parse(savedLanguage);
        setCurrentLanguage(parsed);
      } catch (error) {
        console.error('Error loading language settings:', error);
      }
    }
  }, []);

  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
    localStorage.setItem('piDatingLanguage', JSON.stringify(language));
    
    // Update document language attribute
    document.documentElement.lang = language.code;
    
    // Update document direction for RTL languages
    document.documentElement.dir = isRTLLanguage(language.code) ? 'rtl' : 'ltr';
  };

  const translate = (key: string): string => {
    const languageTranslations = translations[currentLanguage.code];
    return languageTranslations?.[key] || translations.en[key] || key;
  };

  const isRTLLanguage = (code: string): boolean => {
    const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
    return rtlLanguages.includes(code);
  };

  const isRTL = isRTLLanguage(currentLanguage.code);

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      setLanguage,
      translate,
      isRTL
    }}>
      {children}
    </LanguageContext.Provider>
  );
};